import { useFeature } from "flagged"
import React from "react"
import { match } from "ts-pattern"

import styles from "./NoResultsForFilter.module.css"

import { getViewId, isVirtualView } from "~/Views/helpers"

import AddSection from "~/common/AddSection"
import CalendarOutline from "~/common/calendar/CalendarOutline"
import { TangramPerson, TangramProject } from "~/common/react-icons"

import { userViewUpdate } from "~/mutations/User"

import { useViews } from "~/Views/useViews"

import PlannerGrid from "./PlannerGrid"

type FilterProps = {
  subject: "projects" | "people"
  hasFilters: boolean
  localViews?: {
    onSwitchToAllView: () => void
    showAllView: boolean
  }
  onClearFilters?: () => void | undefined
  minimal?: boolean
}

type Props = FilterProps

const NoResultsForFilter = (props: Props) => {
  const { subject, onClearFilters, minimal, localViews, hasFilters } = props

  const { selectedView, availableViews } = useViews()
  const viewsFeatureEnabled = useFeature("pre_filtered_views")

  const hasViews = availableViews.length > 0
  const canAccessAllView = availableViews.some(
    (view) => isVirtualView(view) && view.alias === "ALL",
  )

  const handleSwitchToAllView = async () => {
    if (localViews && localViews.onSwitchToAllView) {
      localViews.onSwitchToAllView()
    } else {
      await userViewUpdate({ alias: "ALL" })
    }
  }

  const showAllView =
    localViews && typeof localViews.showAllView === "boolean"
      ? localViews.showAllView
      : getViewId(selectedView) === "ALL"

  const displayShowAll =
    viewsFeatureEnabled && hasViews && !showAllView && canAccessAllView
  const displayChangeFilters = hasFilters

  const title =
    hasViews && !showAllView
      ? `No ${subject} in '${selectedView.name}'`
      : `No ${subject} found`

  const message = match({ displayShowAll, displayChangeFilters })
    .with({ displayShowAll: true, displayChangeFilters: true }, () => (
      <>
        <span className={styles.link} onClick={handleSwitchToAllView}>
          Switch view to All
        </span>{" "}
        or change the search criteria
      </>
    ))
    .with(
      { displayShowAll: false, displayChangeFilters: true },
      () => "Try changing the filters or search term",
    )
    .with({ displayShowAll: true, displayChangeFilters: false }, () => (
      <>
        <span className={styles.link} onClick={handleSwitchToAllView}>
          Switch view to All
        </span>
      </>
    ))
    .with({ displayShowAll: false, displayChangeFilters: false }, () => <></>)
    .exhaustive()

  if (minimal) {
    return (
      <div className={styles.minimalContainer}>
        <h3 className={styles.minimalHeading}>{title}</h3>
        <p className={styles.minimalParagraph}>{message}</p>
        {hasFilters && onClearFilters && (
          <button
            className={styles.clearFiltersButton}
            onClick={onClearFilters}
          >
            Clear Filters
          </button>
        )}
      </div>
    )
  }

  return (
    <>
      <PlannerGrid>
        <div className={styles.container}>
          {subject === "projects" ? <TangramProject /> : <TangramPerson />}
          <div className={styles.heading}>{title}</div>
          <div className={styles.paragraph}>{message}</div>
          {hasFilters && onClearFilters && (
            <button
              className={styles.clearFiltersButton}
              onClick={onClearFilters}
            >
              Clear Filters
            </button>
          )}
        </div>
        <div style={{ display: "flex" }}>
          <CalendarOutline type="standard" />
        </div>
      </PlannerGrid>
      <div style={{ flex: 1, display: "flex" }}>
        <AddSection />
      </div>
    </>
  )
}

export default NoResultsForFilter
